/**
 * KYC Products Management Component
 *
 * Products are stored in Firestore at: config/kyc_configuration/kyc_products
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCopy,
  IconEdit,
  IconEye,
  IconFileDescription,
  IconFileTypePdf,
  IconRefresh,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { useFirestore, useFunctions } from "../../helpers/firebaseContext";
import {
  doc,
  getDocs,
  collection,
  deleteDoc,
  runTransaction,
} from "firebase/firestore";
import { modals } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import type {
  ComponentOption,
  ComponentSection,
  GenerateContractResult,
  GenerateKYCProductPreviewPDFProps,
  ItemReference,
  KYCPage,
  KYCProduct,
  PageAction,
  SectionField,
} from "../../types";
import { KYCPagePreview, isKycVisible } from "./kyc_pages";
import { KYCProductPdfTextEditor } from "./components/KYCProductPdfTextEditor";

const FIRESTORE_PATHS = {
  CONFIG: "config",
  KYC_CONFIG: "kyc_configuration",
  KYC_PRODUCTS: "kyc_products",
  KYC_PAGES: "kyc_pages",
} as const;

const ROUTES = {
  ADD_PRODUCT_NEW: "/kyc_configuration/add_product/new",
  ADD_PRODUCT: (id: string) => `/kyc_configuration/add_product/${id}`,
} as const;

const NOTIFICATIONS = {
  LOAD_ERROR: {
    color: "red",
    title: "Error Loading Products",
    message: "Failed to load KYC products. Please try again.",
    autoClose: 5000,
  },
  DELETE_SUCCESS: (title: string) => ({
    color: "teal",
    title: "Success",
    message: `"${title}" has been deleted successfully`,
    autoClose: 3000,
  }),
  DELETE_ERROR: {
    color: "red",
    title: "Deletion Failed",
    message: "Failed to delete product. Please try again.",
    autoClose: 5000,
  },
  DUPLICATE_SUCCESS: {
    color: "teal",
    title: "Success",
    message: "Product duplicated successfully",
    autoClose: 3000,
  },
  DUPLICATE_ERROR: {
    color: "red",
    title: "Duplication Failed",
    message: "Failed to duplicate product. Please try again.",
    autoClose: 5000,
  },
  PREVIEW_PDF_SUCCESS: {
    color: "teal",
    title: "Preview PDF Ready",
    message: "Empty PDF preview has opened in a new tab.",
    autoClose: 3000,
  },
  PREVIEW_PDF_ERROR: {
    color: "red",
    title: "Preview PDF Failed",
    message: "Failed to generate empty PDF preview. Please try again.",
    autoClose: 5000,
  },
  INVALID_ID: {
    color: "red",
    title: "Error",
    message: "Product ID cannot be empty",
    autoClose: 5000,
  },
  DUPLICATE_ID: (id: string) => ({
    color: "red",
    title: "Error",
    message: `A product with ID "${id}" already exists`,
    autoClose: 5000,
  }),
} as const;

const LIST_STYLES = {
  metric: {
    border: "1px solid #e9ecef",
    borderRadius: 8,
    padding: "12px 14px",
    background: "#fff",
  },
  mono: {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  emptyCell: {
    padding: "48px 16px",
    textAlign: "center" as const,
  },
} as const;

type ProductReferenceCounts = {
  total: number;
  directPages: number;
  nestedProducts: number;
};

type ProductMetrics = {
  products: number;
  references: number;
  pdfPages: number;
  versionedProducts: number;
};

type ProductItemReference = ItemReference | string;

export type ProductPreviewPage = {
  page: KYCPage;
  sourceProductId: string;
  sourceProductTitle: string;
  referenceId: string;
  depth: number;
  order: number;
};

type ProductPreviewResolution = {
  pages: ProductPreviewPage[];
  warnings: string[];
};

const MAX_PRODUCT_PREVIEW_DEPTH = 10;
const SPECIAL_APP_PAGE_IDS = new Set(["kyc_requirements", "paci_authentication", "docusign_signing"]);
const PLACEHOLDER_PATTERN = /\{\{(.*?)\}\}/g;

const sortProductsByTitle = (products: KYCProduct[]): KYCProduct[] => {
  return [...products].sort((a, b) =>
    getProductTitle(a).localeCompare(getProductTitle(b)),
  );
};

const getProductTitle = (product: KYCProduct): string => {
  return product.title?.trim() || product.id;
};

const getProductDescription = (product: KYCProduct): string => {
  return product.description?.trim() || "No description provided";
};

const getPageCountLabel = (count: number): string => {
  return count === 1 ? "page" : "pages";
};

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : "Unknown error occurred";
};

const normalizeSearchValue = (value: string): string =>
  value.trim().toLowerCase();

const getProductReferences = (product: KYCProduct): ProductItemReference[] =>
  (Array.isArray(product.pages) ? product.pages : []) as ProductItemReference[];

const getReferenceId = (reference: ProductItemReference): string =>
  typeof reference === "string" ? reference : reference.id;

const getReferenceType = (
  reference: ProductItemReference,
): ItemReference["type"] => (typeof reference === "string" ? "page" : reference.type);

const getProductReferenceCounts = (
  product: KYCProduct,
): ProductReferenceCounts => {
  const references = getProductReferences(product);

  return references.reduce(
    (counts, reference) => ({
      total: counts.total + 1,
      directPages:
        counts.directPages + (getReferenceType(reference) === "page" ? 1 : 0),
      nestedProducts:
        counts.nestedProducts +
        (getReferenceType(reference) === "product" ? 1 : 0),
    }),
    { total: 0, directPages: 0, nestedProducts: 0 },
  );
};

const productMatchesSearch = (product: KYCProduct, search: string): boolean => {
  const query = normalizeSearchValue(search);

  if (!query) {
    return true;
  }

  const referenceIds = getProductReferences(product).map(getReferenceId).join(" ");
  const pdfPageIds = (product.pdf_page_index || []).join(" ");
  const searchText = [
    product.id,
    product.title,
    product.title_ar,
    product.description,
    product.description_ar,
    product.version,
    referenceIds,
    pdfPageIds,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchText.includes(query);
};

const generateDuplicateId = (baseId: string): string => {
  return `${baseId}_copy_${Date.now()}`;
};

const isUniqueProductId = (id: string, products: KYCProduct[]): boolean => {
  return !products.some((p) => p.id === id);
};

const createDuplicatedProduct = (
  product: KYCProduct,
  newId: string,
): KYCProduct => ({
  ...product,
  id: newId,
  title: `${product.title} (Copy)`,
});

const cloneOverrideMap = (overrides: Map<string, string>): Map<string, string> =>
  new Map(overrides);

const cloneHiddenSectionsMap = (
  hiddenSections: Map<string, string[]>,
): Map<string, string[]> =>
  new Map(
    Array.from(hiddenSections.entries()).map(([pageId, sectionIds]) => [
      pageId,
      [...sectionIds],
    ]),
  );

const cloneSkippedPageIds = (skippedPageIds: Set<string>): Set<string> =>
  new Set(skippedPageIds);

const clonePageVariablesOverrideMap = (
  pageVariablesOverrides: Map<string, Record<string, string>>,
): Map<string, Record<string, string>> =>
  new Map(
    Array.from(pageVariablesOverrides.entries()).map(([pageId, pageVariables]) => [
      pageId,
      { ...pageVariables },
    ]),
  );

const cleanPageVariables = (
  pageVariables?: Record<string, string>,
): Record<string, string> => {
  if (!pageVariables) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(pageVariables).filter(
      ([, value]) => typeof value === "string" && value !== "",
    ),
  );
};

const mergeReferenceOverrides = (
  overrides: Map<string, string>,
  reference: ProductItemReference,
): void => {
  if (typeof reference === "string") {
    return;
  }

  reference.override_pages?.forEach((override) => {
    overrides.set(override.page_id, override.override_page_id);
  });
};

const mergeReferenceHiddenSections = (
  hiddenSections: Map<string, string[]>,
  reference: ProductItemReference,
): void => {
  if (typeof reference === "string") {
    return;
  }

  reference.hide_sections?.forEach((hiddenSection) => {
    const existing = hiddenSections.get(hiddenSection.page_id) || [];
    hiddenSections.set(hiddenSection.page_id, [
      ...new Set([...existing, ...hiddenSection.section_id]),
    ]);
  });
};

const mergeReferenceSkippedPages = (
  skippedPageIds: Set<string>,
  reference: ProductItemReference,
): void => {
  if (typeof reference === "string") {
    return;
  }

  reference.skip_pages?.forEach((skipPage) => {
    if (skipPage.is_skip_kyc) {
      skippedPageIds.add(skipPage.page_id);
    }
  });
};

const mergeReferencePageVariablesOverrides = (
  pageVariablesOverrides: Map<string, Record<string, string>>,
  reference: ProductItemReference,
): void => {
  if (typeof reference === "string") {
    return;
  }

  reference.pageVariablesOverride?.forEach((pageVariablesOverride) => {
    const existing =
      pageVariablesOverrides.get(pageVariablesOverride.page_id) || {};
    pageVariablesOverrides.set(pageVariablesOverride.page_id, {
      ...existing,
      ...cleanPageVariables(pageVariablesOverride.pageVariables),
    });
  });
};

const getNestedValue = (
  source: Record<string, unknown>,
  key: string,
): unknown => {
  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  return key
    .split(".")
    .map((part) => part.trim())
    .reduce<unknown>((current, part) => {
      if (
        current &&
        typeof current === "object" &&
        Object.prototype.hasOwnProperty.call(current, part)
      ) {
        return (current as Record<string, unknown>)[part];
      }

      return undefined;
    }, source);
};

const replacePreviewPlaceholders = (
  value: string | undefined,
  pageVariables: Record<string, string>,
): string | undefined => {
  if (!value || !value.includes("{{")) {
    return value;
  }

  return value.replace(PLACEHOLDER_PATTERN, (match, key) => {
    const replacement = getNestedValue(pageVariables, key.trim());
    return replacement !== undefined && replacement !== null && replacement !== ""
      ? String(replacement)
      : match;
  });
};

const replacePreviewValuePlaceholders = (
  value: SectionField["value"],
  pageVariables: Record<string, string>,
): SectionField["value"] => {
  return typeof value === "string"
    ? replacePreviewPlaceholders(value, pageVariables)
    : value;
};

const applyPreviewPlaceholdersToOption = (
  option: ComponentOption,
  pageVariables: Record<string, string>,
): ComponentOption => ({
  ...option,
  label: replacePreviewPlaceholders(option.label, pageVariables) || option.label,
  label_ar:
    replacePreviewPlaceholders(option.label_ar, pageVariables) ||
    option.label_ar,
  placeholder: replacePreviewPlaceholders(option.placeholder, pageVariables),
  placeholder_ar: replacePreviewPlaceholders(
    option.placeholder_ar,
    pageVariables,
  ),
  section_fields: option.section_fields?.map((field) =>
    applyPreviewPlaceholdersToField(field, pageVariables),
  ),
});

const applyPreviewPlaceholdersToField = (
  field: SectionField,
  pageVariables: Record<string, string>,
): SectionField => ({
  ...field,
  label: replacePreviewPlaceholders(field.label, pageVariables),
  label_ar: replacePreviewPlaceholders(field.label_ar, pageVariables),
  placeholder: replacePreviewPlaceholders(field.placeholder, pageVariables),
  placeholder_ar: replacePreviewPlaceholders(
    field.placeholder_ar,
    pageVariables,
  ),
  value: replacePreviewValuePlaceholders(field.value, pageVariables),
  value_ar: replacePreviewValuePlaceholders(field.value_ar, pageVariables),
  options: field.options?.map((option) =>
    applyPreviewPlaceholdersToOption(option, pageVariables),
  ),
});

const applyPreviewPlaceholdersToSection = (
  section: ComponentSection,
  pageVariables: Record<string, string>,
): ComponentSection => ({
  ...section,
  section_title:
    replacePreviewPlaceholders(section.section_title, pageVariables) ||
    section.section_title,
  section_title_ar:
    replacePreviewPlaceholders(section.section_title_ar, pageVariables) ||
    section.section_title_ar,
  section_description:
    replacePreviewPlaceholders(section.section_description, pageVariables) ||
    section.section_description,
  section_description_ar:
    replacePreviewPlaceholders(section.section_description_ar, pageVariables) ||
    section.section_description_ar,
  section_fields: section.section_fields?.map((field) =>
    applyPreviewPlaceholdersToField(field, pageVariables),
  ),
});

const applyPreviewPlaceholdersToAction = (
  action: PageAction,
  pageVariables: Record<string, string>,
): PageAction => ({
  ...action,
  label: replacePreviewPlaceholders(action.label, pageVariables) || action.label,
  label_ar:
    replacePreviewPlaceholders(action.label_ar, pageVariables) ||
    action.label_ar,
});

const applyPreviewPlaceholdersToPage = (
  page: KYCPage,
  pageVariables: Record<string, string>,
): KYCPage => ({
  ...page,
  title: replacePreviewPlaceholders(page.title, pageVariables) || page.title,
  title_ar:
    replacePreviewPlaceholders(page.title_ar, pageVariables) || page.title_ar,
  description: replacePreviewPlaceholders(page.description, pageVariables),
  description_ar: replacePreviewPlaceholders(
    page.description_ar,
    pageVariables,
  ),
  pageVariables: {
    ...cleanPageVariables(page.pageVariables),
    ...pageVariables,
  },
  components: page.components?.map((section) =>
    applyPreviewPlaceholdersToSection(section, pageVariables),
  ),
  action: page.action?.map((action) =>
    applyPreviewPlaceholdersToAction(action, pageVariables),
  ),
});

const getReferenceProductTitle = (
  reference: ProductItemReference,
  fallbackProductTitle: string,
): string => {
  if (typeof reference === "string") {
    return fallbackProductTitle;
  }

  return reference.product_title?.trim() || fallbackProductTitle;
};

const applyHiddenSections = (
  page: KYCPage,
  hiddenSections: Map<string, string[]>,
): KYCPage => {
  const sectionsToHide = hiddenSections.get(page.id);

  if (!sectionsToHide || sectionsToHide.length === 0) {
    return page;
  }

  return {
    ...page,
    components: (page.components || []).filter(
      (section) => !sectionsToHide.includes(section.section_id || ""),
    ),
  };
};

const hasAppVisibleContent = (page: KYCPage): boolean => {
  const visibleSections = (page.components || []).filter((section) =>
    isKycVisible(section.is_visible_kyc),
  );

  return visibleSections.length > 0 || SPECIAL_APP_PAGE_IDS.has(page.id);
};

export const resolveProductPreviewPages = (
  product: KYCProduct,
  products: KYCProduct[],
  pages: KYCPage[],
): ProductPreviewResolution => {
  const productsById = new Map(products.map((item) => [item.id, item]));
  const pagesById = new Map(pages.map((page) => [page.id, page]));
  const resolvedPages: ProductPreviewPage[] = [];
  const warnings: string[] = [];

  const resolveProduct = (
    currentProduct: KYCProduct,
    currentProductTitle: string,
    inheritedOverrides: Map<string, string>,
    inheritedHiddenSections: Map<string, string[]>,
    inheritedPageVariablesOverrides: Map<string, Record<string, string>>,
    inheritedSkippedPageIds: Set<string>,
    visitedProductIds: Set<string>,
    depth: number,
  ): void => {
    if (depth > MAX_PRODUCT_PREVIEW_DEPTH) {
      warnings.push(
        `Stopped resolving "${currentProduct.id}" after ${MAX_PRODUCT_PREVIEW_DEPTH} nested levels.`,
      );
      return;
    }

    if (visitedProductIds.has(currentProduct.id)) {
      warnings.push(`Skipped circular product reference "${currentProduct.id}".`);
      return;
    }

    const nextVisitedProductIds = new Set(visitedProductIds);
    nextVisitedProductIds.add(currentProduct.id);

    getProductReferences(currentProduct).forEach((reference) => {
      const referenceId = getReferenceId(reference);
      const referenceType = getReferenceType(reference);
      const referenceOverrides = cloneOverrideMap(inheritedOverrides);
      const referenceHiddenSections =
        cloneHiddenSectionsMap(inheritedHiddenSections);
      const referencePageVariablesOverrides =
        clonePageVariablesOverrideMap(inheritedPageVariablesOverrides);
      const referenceSkippedPageIds = cloneSkippedPageIds(
        inheritedSkippedPageIds,
      );

      mergeReferenceOverrides(referenceOverrides, reference);
      mergeReferenceHiddenSections(referenceHiddenSections, reference);
      mergeReferencePageVariablesOverrides(
        referencePageVariablesOverrides,
        reference,
      );
      mergeReferenceSkippedPages(referenceSkippedPageIds, reference);

      if (referenceType === "page") {
        const resolvedPageId = referenceOverrides.get(referenceId) || referenceId;

        if (
          (typeof reference !== "string" && reference.is_skip_kyc) ||
          referenceSkippedPageIds.has(referenceId) ||
          referenceSkippedPageIds.has(resolvedPageId)
        ) {
          return;
        }

        const page = pagesById.get(resolvedPageId);

        if (!page) {
          warnings.push(`Page "${resolvedPageId}" was not found.`);
          return;
        }

        const mergedPageVariables = {
          ...cleanPageVariables(page.pageVariables),
          ...cleanPageVariables(
            referencePageVariablesOverrides.get(referenceId),
          ),
          ...cleanPageVariables(
            typeof reference === "string" ? undefined : reference.pageVariables,
          ),
        };
        const previewPage = applyPreviewPlaceholdersToPage(
          applyHiddenSections(page, referenceHiddenSections),
          mergedPageVariables,
        );

        if (!hasAppVisibleContent(previewPage)) {
          return;
        }

        resolvedPages.push({
          page: previewPage,
          sourceProductId: currentProduct.id,
          sourceProductTitle: getReferenceProductTitle(
            reference,
            currentProductTitle,
          ),
          referenceId,
          depth,
          order: resolvedPages.length + 1,
        });
        return;
      }

      const nestedProduct = productsById.get(referenceId);

      if (!nestedProduct) {
        warnings.push(`Nested product "${referenceId}" was not found.`);
        return;
      }

      const nestedProductTitle = getReferenceProductTitle(
        reference,
        getProductTitle(nestedProduct),
      );

      resolveProduct(
        nestedProduct,
        nestedProductTitle,
        referenceOverrides,
        referenceHiddenSections,
        referencePageVariablesOverrides,
        referenceSkippedPageIds,
        nextVisitedProductIds,
        depth + 1,
      );
    });
  };

  resolveProduct(
    product,
    getProductTitle(product),
    new Map(),
    new Map(),
    new Map(),
    new Set(),
    new Set(),
    0,
  );

  return { pages: resolvedPages, warnings };
};

const createPdfObjectUrl = (pdfBase64: string): string => {
  const binary = window.atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

const openPdfPreview = (pdfBase64: string, previewWindow: Window | null): void => {
  const objectUrl = createPdfObjectUrl(pdfBase64);

  if (previewWindow) {
    previewWindow.location.href = objectUrl;
  } else {
    window.open(objectUrl, "_blank", "noopener,noreferrer");
  }

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

const SummaryMetric = ({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) => (
  <div style={LIST_STYLES.metric}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
      {label}
    </Text>
    <Text size="xl" fw={800}>
      {value}
    </Text>
  </div>
);

const KYCProductPreview = ({
  product,
  products,
  pages,
}: {
  product: KYCProduct;
  products: KYCProduct[];
  pages: KYCPage[];
}) => {
  const functions = useFunctions();
  const [activeIndex, setActiveIndex] = useState(0);
  const [pdfLang, setPdfLang] = useState<"ar" | "en">("ar");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [previewScrollRatio, setPreviewScrollRatio] = useState(0);

  const resolution = useMemo(
    () => resolveProductPreviewPages(product, products, pages),
    [product, products, pages],
  );
  const activePreview = resolution.pages[activeIndex] || resolution.pages[0];

  useEffect(() => {
    if (activeIndex >= resolution.pages.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, resolution.pages.length]);

  useEffect(() => {
    setPreviewScrollRatio(0);
  }, [activeIndex, activePreview?.page.id]);

  const handlePreviousPage = useCallback(() => {
    setActiveIndex((currentIndex) => Math.max(currentIndex - 1, 0));
  }, []);

  const handleNextPage = useCallback(() => {
    setActiveIndex((currentIndex) =>
      Math.min(currentIndex + 1, Math.max(resolution.pages.length - 1, 0)),
    );
  }, [resolution.pages.length]);

  const previewNavigation = {
    current: activeIndex + 1,
    total: resolution.pages.length,
    hasPrevious: activeIndex > 0,
    hasNext: activeIndex < resolution.pages.length - 1,
    onPrevious: handlePreviousPage,
    onNext: handleNextPage,
  };

  const handleGenerateEmptyPdf = useCallback(async () => {
    const previewWindow = window.open("", "_blank", "noopener,noreferrer");

    try {
      setIsGeneratingPdf(true);
      const generatePreviewPDF = httpsCallable<
        GenerateKYCProductPreviewPDFProps,
        GenerateContractResult
      >(functions, "generateKYCProductPreviewPDF");
      const result = await generatePreviewPDF({
        productId: product.id,
        baseUrl: window.location.origin,
        lang: pdfLang,
        showQRCodePDF: false,
      });

      openPdfPreview(result.data.pdfBase64, previewWindow);
      showNotification(NOTIFICATIONS.PREVIEW_PDF_SUCCESS);
    } catch (error) {
      previewWindow?.close();
      console.error("Error generating product preview PDF:", error);
      showNotification(NOTIFICATIONS.PREVIEW_PDF_ERROR);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [functions, pdfLang, product.id]);

  return (
    <Stack gap="md">
      <Paper withBorder radius="md" p="md" bg="gray.0">
        <Group justify="space-between" align="flex-start" gap="md">
          <div style={{ flex: 1 }}>
            <Group gap="xs" mb={6}>
              <Text fw={800}>{getProductTitle(product)}</Text>
              {product.version && (
                <Badge variant="light" color="green">
                  v{product.version}
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {resolution.pages.length} app-visible{" "}
              {getPageCountLabel(resolution.pages.length)} in the product flow.
            </Text>
          </div>

          <Stack gap="xs" align="flex-end">
            <SegmentedControl
              size="xs"
              value={pdfLang}
              onChange={(value) => setPdfLang(value as "ar" | "en")}
              data={[
                { label: "AR", value: "ar" },
                { label: "EN", value: "en" },
              ]}
            />
            <Button
              size="xs"
              leftSection={<IconFileTypePdf size={16} />}
              loading={isGeneratingPdf}
              onClick={() => void handleGenerateEmptyPdf()}
            >
              Empty PDF
            </Button>
          </Stack>
        </Group>
      </Paper>

      {resolution.warnings.length > 0 && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
        >
          <Stack gap={4}>
            {resolution.warnings.slice(0, 4).map((warning) => (
              <Text size="sm" key={warning}>
                {warning}
              </Text>
            ))}
            {resolution.warnings.length > 4 && (
              <Text size="sm" c="dimmed">
                +{resolution.warnings.length - 4} more warnings
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      {resolution.pages.length === 0 ? (
        <Paper withBorder radius="md" p="xl" style={{ textAlign: "center" }}>
          <Text fw={700}>No app-visible pages found</Text>
          <Text size="sm" c="dimmed" mt={4}>
            This product has no referenced pages with app-visible sections.
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, xl: 3 }} spacing="md">
          <Paper withBorder radius="md" p="sm">
            <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="xs">
              App flow
            </Text>
            <ScrollArea.Autosize mah={640}>
              <Stack gap={6}>
                {resolution.pages.map((previewPage, index) => (
                  <Button
                    key={`${previewPage.sourceProductId}-${previewPage.referenceId}-${previewPage.page.id}-${index}`}
                    variant={activeIndex === index ? "light" : "subtle"}
                    color={activeIndex === index ? "blue" : "gray"}
                    justify="flex-start"
                    h="auto"
                    py="xs"
                    px="sm"
                    onClick={() => setActiveIndex(index)}
                    styles={{ inner: { justifyContent: "flex-start" } }}
                  >
                    <Stack gap={2} align="flex-start">
                      <Group gap={6} wrap="nowrap">
                        <Badge size="xs" variant="filled">
                          {previewPage.order}
                        </Badge>
                        <Text size="sm" fw={700} lineClamp={1}>
                          {previewPage.page.title || previewPage.page.id}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {previewPage.sourceProductTitle}
                      </Text>
                      <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
                        {previewPage.page.id}
                      </Text>
                    </Stack>
                  </Button>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          </Paper>

          <Stack gap="xs">
            <Group justify="space-between" gap="xs">
              <Badge variant="light" color="blue">
                English
              </Badge>
              <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
                EN
              </Text>
            </Group>
            {activePreview && (
              <KYCPagePreview
                page={activePreview.page}
                showIntro={false}
                language="en"
                navigation={previewNavigation}
                scrollRatio={previewScrollRatio}
                onScrollRatioChange={setPreviewScrollRatio}
              />
            )}
          </Stack>

          <Stack gap="xs">
            <Group justify="space-between" gap="xs" dir="rtl">
              <Badge variant="light" color="green">
                العربية
              </Badge>
              <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
                AR
              </Text>
            </Group>
            {activePreview && (
              <KYCPagePreview
                page={activePreview.page}
                showIntro={false}
                language="ar"
                navigation={previewNavigation}
                scrollRatio={previewScrollRatio}
                onScrollRatioChange={setPreviewScrollRatio}
              />
            )}
          </Stack>
        </SimpleGrid>
      )}
    </Stack>
  );
};

export const useKYCProducts = (db: any) => {
  const [products, setProducts] = useState<KYCProduct[]>([]);
  const [pages, setPages] = useState<KYCPage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadProducts = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const [productsSnapshot, pagesSnapshot] = await Promise.all([
        getDocs(
          collection(
            db,
            FIRESTORE_PATHS.CONFIG,
            FIRESTORE_PATHS.KYC_CONFIG,
            FIRESTORE_PATHS.KYC_PRODUCTS,
          ),
        ),
        getDocs(
          collection(
            db,
            FIRESTORE_PATHS.CONFIG,
            FIRESTORE_PATHS.KYC_CONFIG,
            FIRESTORE_PATHS.KYC_PAGES,
          ),
        ),
      ]);

      const productsData: KYCProduct[] = productsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as KYCProduct,
      );
      const pagesData: KYCPage[] = pagesSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as KYCPage,
      );

      setProducts(sortProductsByTitle(productsData));
      setPages(pagesData);
    } catch (error) {
      console.error("Error loading products:", error);
      setError(getErrorMessage(error));
      showNotification(NOTIFICATIONS.LOAD_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return { products, pages, isLoading, error, reloadProducts: loadProducts };
};

const useProductOperations = (
  db: any,
  products: KYCProduct[],
  reloadProducts: () => Promise<void>,
) => {
  const navigate = useNavigate();

  const handleDelete = useCallback(
    (productId: string, productTitle: string): void => {
      modals.openConfirmModal({
        title: "Delete KYC Product",
        centered: true,
        children: (
          <Text size="sm">
            Are you sure you want to delete <strong>"{productTitle}"</strong>?
            This action cannot be undone and will remove all associated
            configurations.
          </Text>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          try {
            const docRef = doc(
              db,
              FIRESTORE_PATHS.CONFIG,
              FIRESTORE_PATHS.KYC_CONFIG,
              FIRESTORE_PATHS.KYC_PRODUCTS,
              productId,
            );
            await deleteDoc(docRef);

            showNotification(NOTIFICATIONS.DELETE_SUCCESS(productTitle));
            await reloadProducts();
          } catch (error) {
            console.error("Error deleting product:", error);
            showNotification(NOTIFICATIONS.DELETE_ERROR);
          }
        },
      });
    },
    [db, reloadProducts],
  );

  const handleEdit = useCallback(
    (productId: string): void => {
      navigate(ROUTES.ADD_PRODUCT(productId));
    },
    [navigate],
  );

  const handleCreateNew = useCallback((): void => {
    navigate(ROUTES.ADD_PRODUCT_NEW);
  }, [navigate]);

  const handleDuplicate = useCallback(
    (product: KYCProduct): void => {
      const defaultNewId = generateDuplicateId(product.id);

      modals.openConfirmModal({
        title: "Duplicate KYC Product",
        centered: true,
        children: (
          <Stack gap="md">
            <Text size="sm">
              You are about to duplicate <strong>"{product.title}"</strong>
            </Text>
            <div>
              <Text size="sm" fw={500} mb="xs">
                New Product ID:
              </Text>
              <input
                id="duplicate-product-id"
                type="text"
                defaultValue={defaultNewId}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "monospace",
                }}
              />
              <Text size="xs" c="dimmed" mt="xs">
                Enter a unique ID for the duplicated product
              </Text>
            </div>
          </Stack>
        ),
        labels: { confirm: "Duplicate", cancel: "Cancel" },
        confirmProps: { color: "blue" },
        onConfirm: async () => {
          const inputElement = document.getElementById(
            "duplicate-product-id",
          ) as HTMLInputElement;
          const newProductId = inputElement?.value.trim() || defaultNewId;
          try {
            if (!newProductId) {
              showNotification(NOTIFICATIONS.INVALID_ID);
              return;
            }

            if (!isUniqueProductId(newProductId, products)) {
              showNotification(NOTIFICATIONS.DUPLICATE_ID(newProductId));
              return;
            }

            const duplicatedProduct = createDuplicatedProduct(
              product,
              newProductId,
            );

            const newDocRef = doc(
              db,
              FIRESTORE_PATHS.CONFIG,
              FIRESTORE_PATHS.KYC_CONFIG,
              FIRESTORE_PATHS.KYC_PRODUCTS,
              duplicatedProduct.id,
            );
            await runTransaction(db, async (transaction) => {
              const existing = await transaction.get(newDocRef);

              if (existing.exists()) {
                throw new Error(
                  `Product ID "${newProductId}" already exists in Firestore`,
                );
              }

              transaction.set(newDocRef, duplicatedProduct);
            });

            showNotification(NOTIFICATIONS.DUPLICATE_SUCCESS);
            await reloadProducts();
          } catch (error: any) {
            console.error("Error duplicating product:", error);
            if (error.message?.includes("already exists")) {
              showNotification(NOTIFICATIONS.DUPLICATE_ID(newProductId));
            } else {
              showNotification(NOTIFICATIONS.DUPLICATE_ERROR);
            }
          }
        },
      });
    },
    [db, reloadProducts, products],
  );

  return { handleDelete, handleEdit, handleCreateNew, handleDuplicate };
};

const KYCProducts: React.FC = () => {
  const db = useFirestore();
  const [search, setSearch] = useState("");
  const { products, pages, isLoading, error, reloadProducts } =
    useKYCProducts(db);
  const { handleDelete, handleEdit, handleCreateNew, handleDuplicate } =
    useProductOperations(db, products, reloadProducts);

  const handlePreview = useCallback(
    (product: KYCProduct): void => {
      modals.open({
        title: `Preview: ${getProductTitle(product)}`,
        size: "95rem",
        children: (
          <KYCProductPreview product={product} products={products} pages={pages} />
        ),
      });
    },
    [pages, products],
  );

  const handlePdfTextEditor = useCallback(
    (product: KYCProduct): void => {
      modals.open({
        title: `Edit PDF text: ${getProductTitle(product)}`,
        size: "98rem",
        children: (
          <KYCProductPdfTextEditor
            product={product}
            products={products}
            pages={pages}
            onSaved={reloadProducts}
          />
        ),
      });
    },
    [pages, products, reloadProducts],
  );

  const metrics = useMemo<ProductMetrics>(() => {
    return products.reduce(
      (totals, product) => {
        const references = getProductReferenceCounts(product);
        return {
          products: totals.products + 1,
          references: totals.references + references.total,
          pdfPages: totals.pdfPages + (product.pdf_page_index?.length || 0),
          versionedProducts:
            totals.versionedProducts + (product.version ? 1 : 0),
        };
      },
      { products: 0, references: 0, pdfPages: 0, versionedProducts: 0 },
    );
  }, [products]);

  const filteredProducts = useMemo(
    () => products.filter((product) => productMatchesSearch(product, search)),
    [products, search],
  );

  if (isLoading) {
    return (
      <main style={{ padding: "1rem 0" }}>
        <Group justify="center" mt="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading KYC products...</Text>
          </Stack>
        </Group>
      </main>
    );
  }

  if (error) {
    return (
      <main style={{ padding: "1rem 0" }}>
        <Card withBorder p="xl" style={{ textAlign: "center" }}>
          <Text c="red" size="lg" fw={600} mb="md">
            Error Loading Products
          </Text>
          <Text c="dimmed" mb="lg">
            {error}
          </Text>
          <Button onClick={reloadProducts}>Retry</Button>
        </Card>
      </main>
    );
  }

  if (products.length === 0) {
    return (
      <main style={{ padding: "1rem 0" }}>
        <Card withBorder p="xl" style={{ textAlign: "center" }}>
          <Stack align="center" gap="md">
            <IconFileDescription size={44} style={{ opacity: 0.35 }} />
            <div>
              <Text size="lg" fw={600} mb="xs">
                No KYC products found
              </Text>
              <Text c="dimmed" mb="lg">
                Create the first product to start configuring KYC flows.
              </Text>
            </div>
            <Button
              leftSection={<IconFileDescription size={16} />}
              onClick={handleCreateNew}
            >
              Create Product
            </Button>
          </Stack>
        </Card>
      </main>
    );
  }

  return (
    <main style={{ padding: "1rem 0" }}>
      <Stack gap="md">
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" align="flex-end" gap="md">
            <div>
              <Text size="lg" fw={700}>
                Products
              </Text>
              <Text size="sm" c="dimmed">
                Showing {filteredProducts.length} of {products.length}
              </Text>
            </div>

            <Group
              gap="xs"
              wrap="nowrap"
              style={{ flex: "1 1 420px", justifyContent: "flex-end" }}
            >
              <TextInput
                aria-label="Search KYC products"
                placeholder="Search products, IDs, page IDs, versions"
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                style={{ width: "min(520px, 100%)" }}
              />
              <Tooltip label="Reload products">
                <ActionIcon
                  variant="light"
                  size="lg"
                  aria-label="Reload products"
                  onClick={() => void reloadProducts()}
                >
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" mt="md">
            <SummaryMetric label="Products" value={metrics.products} />
            <SummaryMetric label="References" value={metrics.references} />
            <SummaryMetric label="PDF Pages" value={metrics.pdfPages} />
            <SummaryMetric
              label="Versioned"
              value={metrics.versionedProducts}
            />
          </SimpleGrid>
        </Paper>

        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table.ScrollContainer minWidth={980}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>References</Table.Th>
                  <Table.Th>PDF</Table.Th>
                  <Table.Th>First items</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredProducts.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5} style={LIST_STYLES.emptyCell}>
                      <Stack align="center" gap="sm">
                        <Text fw={600}>No products match this search</Text>
                        <Button variant="subtle" onClick={() => setSearch("")}>
                          Clear search
                        </Button>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredProducts.map((product) => {
                    const referenceCounts = getProductReferenceCounts(product);
                    const references = getProductReferences(product);
                    const visibleReferences = references.slice(0, 3);
                    const hiddenReferenceCount = Math.max(
                      references.length - visibleReferences.length,
                      0,
                    );

                    return (
                      <Table.Tr key={product.id}>
                        <Table.Td style={{ minWidth: 330 }}>
                          <Stack gap={5}>
                            <Group gap="xs" wrap="nowrap">
                              <Text fw={700} lineClamp={1}>
                                {getProductTitle(product)}
                              </Text>
                              {product.version && (
                                <Badge variant="light" color="green" size="sm">
                                  v{product.version}
                                </Badge>
                              )}
                            </Group>
                            <Badge
                              variant="light"
                              color="gray"
                              size="sm"
                              style={{
                                ...LIST_STYLES.mono,
                                alignSelf: "flex-start",
                              }}
                            >
                              {product.id}
                            </Badge>
                            <Text size="sm" c="dimmed" lineClamp={2}>
                              {getProductDescription(product)}
                            </Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap={4}>
                            <Group gap={6}>
                              <Badge variant="light" color="blue">
                                {referenceCounts.directPages}{" "}
                                {getPageCountLabel(referenceCounts.directPages)}
                              </Badge>
                              {referenceCounts.nestedProducts > 0 && (
                                <Badge variant="light" color="violet">
                                  {referenceCounts.nestedProducts} products
                                </Badge>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              {referenceCounts.total} total references
                            </Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={700}>
                            {product.pdf_page_index?.length || 0}
                          </Text>
                          <Text size="xs" c="dimmed">
                            PDF pages
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ minWidth: 230 }}>
                          {visibleReferences.length === 0 ? (
                            <Text size="sm" c="dimmed">
                              No references
                            </Text>
                          ) : (
                            <Stack gap={4}>
                              {visibleReferences.map((reference) => (
                                <Group
                                  key={`${product.id}-${getReferenceType(reference)}-${getReferenceId(reference)}`}
                                  gap={6}
                                  wrap="nowrap"
                                >
                                  <Badge
                                    size="xs"
                                    variant="outline"
                                    color={
                                      getReferenceType(reference) === "page"
                                        ? "blue"
                                        : "violet"
                                    }
                                  >
                                    {getReferenceType(reference)}
                                  </Badge>
                                  <Text
                                    size="xs"
                                    lineClamp={1}
                                    style={LIST_STYLES.mono}
                                  >
                                    {getReferenceId(reference)}
                                  </Text>
                                </Group>
                              ))}
                              {hiddenReferenceCount > 0 && (
                                <Text size="xs" c="dimmed">
                                  +{hiddenReferenceCount} more
                                </Text>
                              )}
                            </Stack>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            <Tooltip label="Preview app flow">
                              <ActionIcon
                                variant="subtle"
                                color="teal"
                                aria-label={`Preview ${getProductTitle(product)}`}
                                onClick={() => handlePreview(product)}
                              >
                                <IconEye size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Edit PDF text">
                              <ActionIcon
                                variant="subtle"
                                color="grape"
                                aria-label={`Edit PDF text for ${getProductTitle(product)}`}
                                onClick={() => handlePdfTextEditor(product)}
                              >
                                <IconFileTypePdf size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Edit product">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                aria-label={`Edit ${getProductTitle(product)}`}
                                onClick={() => handleEdit(product.id)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Duplicate product">
                              <ActionIcon
                                variant="subtle"
                                color="yellow"
                                aria-label={`Duplicate ${getProductTitle(product)}`}
                                onClick={() => handleDuplicate(product)}
                              >
                                <IconCopy size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete product">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label={`Delete ${getProductTitle(product)}`}
                                onClick={() =>
                                  handleDelete(
                                    product.id,
                                    getProductTitle(product),
                                  )
                                }
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      </Stack>
    </main>
  );
};

export default KYCProducts;
